import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import Button from '../../../common/base/Button';
import { IconName } from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import MultiSelectInput from '../../../common/Form/Fields/MultiSelectInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { default_data_formatter, metric_definitions, MetricName } from '../../../common/metrics/metric-definitions';
import MetricChart from './MetricChart';
import MetricTable, { MetricTableRow } from '../../../common/metrics/MetricTable';
import { MetricConfigs, TableData, useMetric } from './useMetric';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { DashboardContext } from '../DashboardContext';
import useSearchQuery from '../../../hooks/useSearchQuery';
import useViewportBreakpoint from '../../../hooks/useViewportBreakpoint';
import { StyledViewContent, StyledViewLayout } from '../../../layouts/ViewLayout';
import { DateRange, getDateRangeQueryParam } from './MetricDatePicker';
import { useRefreshKey } from './RefreshKeyContext';
export interface MetricCategory {
  label: string;
  icon?: IconName;
  dimension: {
    label: string;
    key: 'source_id' | 'webhook_id' | 'destination_id';
    api_root: 'sources' | 'webhooks' | 'destinations';
  };
  sections: {
    title?: string;
    charts?: MetricName[];
    tables?: MetricName[];
  }[];
}
export const metric_categories: Record<string, MetricCategory> = {
  sources: {
    label: 'Sources',
    icon: 'source',
    dimension: {
      label: 'Sources',
      key: 'source_id',
      api_root: 'sources'
    },
    sections: [{
      charts: ['requests_count', 'requests_rate', 'requests_accepted_count', 'requests_rejected_count', 'requests_discarded_count', 'requests_average_event', 'requests_average_ignored']
    }]
  },
  connections: {
    label: 'Connections',
    icon: 'connections',
    dimension: {
      label: 'Connections',
      key: 'webhook_id',
      api_root: 'webhooks'
    },
    sections: [{
      charts: ['events_count', 'events_rate', 'events_successful_count', 'events_failed_count', 'events_error_rate', 'events_average_attempts'],
      tables: ['events_with_scheduled_retry_count', 'events_paused_count', 'events_delayed_count']
    }]
  },
  destinations: {
    label: 'Destinations',
    icon: 'destination',
    dimension: {
      label: 'Destinations',
      key: 'destination_id',
      api_root: 'destinations'
    },
    sections: [{
      title: 'Events',
      charts: ['events_count', 'events_rate', 'events_successful_count', 'events_failed_count', 'events_error_rate', 'events_average_attempts']
    }, {
      title: 'Queue',
      charts: ['events_oldest_pending', 'events_pending_count']
    }, {
      title: 'Delivery',
      charts: ['attempts_delivered_count', 'attempts_delivered_rate', 'attempts_successful_count', 'attempts_failed_count', 'attempts_error_rate', 'attempts_average_response_time', 'attempts_95_response_time', 'attempts_99_response_time', 'attempts_max_response_time']
    }]
  }
};
export const useMetricsFilters = () => {
  const $ = _c(34);
  const location = useLocation();
  let t0;
  if ($[0] !== location.pathname) {
    t0 = location.pathname.split("/")[2]?.split("#")[0] || Object.keys(metric_categories)[0];
    $[0] = location.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const metric_category_key = t0;
  const metric_category = metric_categories[metric_category_key];
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  let t1;
  if ($[2] !== metric_category.dimension.key || $[3] !== updateSearchQuery) {
    t1 = values => {
      updateSearchQuery({
        [metric_category.dimension.key]: values.length > 0 ? values : undefined
      });
    };
    $[2] = metric_category.dimension.key;
    $[3] = updateSearchQuery;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const onDimentionChanged = t1;
  let t2;
  if ($[5] !== metric_category.dimension.api_root) {
    const getLabel = model => {
      if (metric_category.dimension.api_root === "webhooks") {
        return model.full_name;
      }
      return model.name;
    };
    t2 = data => data && data.models.map(model_0 => ({
      label: getLabel(model_0),
      value: model_0.id
    }));
    $[5] = metric_category.dimension.api_root;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const formatOptions = t2;
  let t3;
  if ($[7] !== metric_category.dimension.api_root) {
    t3 = (search_term, values_0) => {
      if (metric_category.dimension.api_root === "webhooks") {
        return {
          limit: 250,
          full_name: search_term || undefined,
          id: values_0
        };
      }
      return {
        limit: 250,
        name: search_term ? {
          contains: search_term
        } : undefined,
        id: values_0
      };
    };
    $[7] = metric_category.dimension.api_root;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const formatFilters = t3;
  let t4;
  if ($[9] !== metric_category.dimension.key || $[10] !== query) {
    t4 = query[metric_category.dimension.key] || [];
    $[9] = metric_category.dimension.key;
    $[10] = query;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const dimension_values = t4;
  let t5;
  if ($[12] !== dimension_values || $[13] !== metric_category.dimension.key) {
    t5 = {
      [metric_category.dimension.key]: dimension_values
    };
    $[12] = dimension_values;
    $[13] = metric_category.dimension.key;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== metric_category.dimension.key || $[16] !== onDimentionChanged) {
    t6 = values_1 => onDimentionChanged(values_1[metric_category.dimension.key]);
    $[15] = metric_category.dimension.key;
    $[16] = onDimentionChanged;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== HookdeckAPI || $[19] !== formatFilters || $[20] !== formatOptions || $[21] !== metric_category.dimension.api_root || $[22] !== metric_category.dimension.key || $[23] !== metric_category.dimension.label) {
    t7 = t8 => {
      const {
        handleSubmit
      } = t8;
      return <Form><MultiSelectInput renderToggle={(opened, toggle, value) => <Button outline={true} icon="filter_list" onClick={toggle} dropdown={true} active={opened} expand_icon="chevron_down" badge={value?.length > 0 ? {
          label: value.length
        } : undefined}>{metric_category.dimension.label}</Button>} placement="bottom-end" onClose={handleSubmit} onRemove={handleSubmit} initial_options={[]} formatOptions={formatOptions} formatFilters={formatFilters} fetcher={HookdeckAPI[metric_category.dimension.api_root].list} fetcherKey={APIMethodKeys[metric_category.dimension.api_root].list} name={metric_category.dimension.key} m={0} /></Form>;
    };
    $[18] = HookdeckAPI;
    $[19] = formatFilters;
    $[20] = formatOptions;
    $[21] = metric_category.dimension.api_root;
    $[22] = metric_category.dimension.key;
    $[23] = metric_category.dimension.label;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t5 || $[26] !== t6 || $[27] !== t7) {
    t8 = <Formik initialValues={t5} onSubmit={t6}>{t7}</Formik>;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const form = t8;
  let t9;
  if ($[29] !== dimension_values || $[30] !== form || $[31] !== metric_category || $[32] !== metric_category_key) {
    t9 = {
      dimension_values,
      form,
      metric_category_key,
      metric_category,
      metric_categories
    };
    $[29] = dimension_values;
    $[30] = form;
    $[31] = metric_category;
    $[32] = metric_category_key;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
const page_nav_height = 154;
const StyledExpandedMetric = styled(Div)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({
  theme
}) => theme.spacing(6)};
  right: ${({
  theme
}) => theme.spacing(6)};
  height: 100%;
  background-color: ${({
  theme
}) => theme.colors.surface.base.surface};
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const useMetricTableData = (metric, configs) => {
  const $ = _c(29);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team
  } = useContext(DashboardContext);
  const {
    refresh_key
  } = useRefreshKey();
  const {
    type,
    title,
    description,
    resource,
    formatTableTooltipData: formatTooltipData,
    formatDataForDisplay,
    buildExploreLink
  } = metric_definitions[metric];
  const {
    data,
    error,
    rate_as
  } = useMetric("table", metric, refresh_key, configs);
  let t0;
  let t1;
  let unique_ids;
  if ($[0] !== data || $[1] !== resource) {
    unique_ids = data ? data.map(_temp) : [];
    t0 = useSWR;
    t1 = unique_ids.length > 0 ? APIMethodKeys[`${resource}s`].list({
      id: unique_ids
    }) : null;
    $[0] = data;
    $[1] = resource;
    $[2] = t0;
    $[3] = t1;
    $[4] = unique_ids;
  } else {
    t0 = $[2];
    t1 = $[3];
    unique_ids = $[4];
  }
  let t2;
  if ($[5] !== HookdeckAPI || $[6] !== resource || $[7] !== unique_ids) {
    t2 = () => HookdeckAPI[`${resource}s`].list(unique_ids.length > 0 ? {
      id: unique_ids,
      archived: true
    } : {
      limit: 1
    }) as any;
    $[5] = HookdeckAPI;
    $[6] = resource;
    $[7] = unique_ids;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    data: resources
  } = t0(t1, t2);
  let t3;
  if ($[9] !== data || $[10] !== formatDataForDisplay || $[11] !== formatTooltipData || $[12] !== rate_as || $[13] !== resource || $[14] !== resources || $[15] !== team || $[16] !== type || $[17] !== unique_ids.length) {
    const resources_by_id = resources?.models ? resources.models.reduce(_temp2, {}) : {};
    const loading = !data || !resources && unique_ids.length > 0;
    bb0: {
      if (loading) {
        t3 = null;
        break bb0;
      }
      if (!data || data.length === 0) {
        let t4;
        if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = [];
          $[19] = t4;
        } else {
          t4 = $[19];
        }
        t3 = t4;
        break bb0;
      }
      t3 = data.map(row => ({
        key: row.resource_id,
        label: (resource === "webhook" ? resources_by_id[row.resource_id]?.full_name : resources_by_id[row.resource_id]?.name) || row.resource_id,
        value: row.value,
        display_value: formatDataForDisplay ? formatDataForDisplay(row.value) : default_data_formatter[type](row.value, rate_as || undefined),
        tooltip: formatTooltipData ? formatTooltipData(row.value, {
          resource: resources_by_id[row.resource_id],
          rate_limit: team.max_events_per_second
        }) : undefined
      }));
    }
    $[9] = data;
    $[10] = formatDataForDisplay;
    $[11] = formatTooltipData;
    $[12] = rate_as;
    $[13] = resource;
    $[14] = resources;
    $[15] = team;
    $[16] = type;
    $[17] = unique_ids.length;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const rows = t3;
  let t4;
  if ($[20] !== buildExploreLink || $[21] !== configs) {
    t4 = buildExploreLink ? <Button ghost={true} small={true} icon="link" to={buildExploreLink({
      date: getDateRangeQueryParam(configs.date_range),
      ...configs.options?.filters
    })} /> : undefined;
    $[20] = buildExploreLink;
    $[21] = configs;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const header_right = t4;
  const t5 = !!error;
  let t6;
  if ($[23] !== description || $[24] !== header_right || $[25] !== rows || $[26] !== t5 || $[27] !== title) {
    t6 = {
      title,
      description,
      rows,
      error: t5,
      header_right
    };
    $[23] = description;
    $[24] = header_right;
    $[25] = rows;
    $[26] = t5;
    $[27] = title;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
};
const MetricTableItem = t0 => {
  const $ = _c(11);
  let configs;
  let metric;
  let other_props;
  if ($[0] !== t0) {
    ({
      metric,
      configs,
      ...other_props
    } = t0);
    $[0] = t0;
    $[1] = configs;
    $[2] = metric;
    $[3] = other_props;
  } else {
    configs = $[1];
    metric = $[2];
    other_props = $[3];
  }
  const {
    title,
    description,
    rows,
    error,
    header_right
  } = useMetricTableData(metric, configs);
  let t1;
  if ($[4] !== description || $[5] !== error || $[6] !== header_right || $[7] !== other_props || $[8] !== rows || $[9] !== title) {
    t1 = <MetricTable title={title} description={description} rows={rows} error={error} header_right={header_right} {...other_props} />;
    $[4] = description;
    $[5] = error;
    $[6] = header_right;
    $[7] = other_props;
    $[8] = rows;
    $[9] = title;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
};
const MetricCategoryView: React.FC<{
  date_range: DateRange;
}> = t0 => {
  const $ = _c(29);
  const {
    date_range
  } = t0;
  const breakpoint = useViewportBreakpoint();
  const {
    dimension_values,
    metric_category
  } = useMetricsFilters();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const expanded_metric = query.expanded;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mobile: 1,
      tablet: 2,
      laptop: 2,
      desktop: 3
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const columns_layout = t1 as const;
  const columns = columns_layout[breakpoint];
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      position: "relative"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = expanded_metric ? "hidden" : "auto";
  let t4;
  if ($[2] !== t3) {
    t4 = {
      overflow: t3,
      height: `calc(100vh - ${page_nav_height}px)`
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== columns || $[5] !== date_range || $[6] !== dimension_values || $[7] !== metric_category.dimension || $[8] !== metric_category.sections || $[9] !== updateSearchQuery) {
    let t6;
    if ($[11] !== columns || $[12] !== date_range || $[13] !== dimension_values || $[14] !== metric_category.dimension || $[15] !== updateSearchQuery) {
      t6 = (section, i) => <>{section.title && <Text as="h2" size="l" bold={true} m={{
          b: 4,
          t: i === 0 ? 0 : 12
        }}>{section.title}</Text>}{section.charts && <Div grid={{
          columns,
          gap: 6
        }}>{section.charts.map(metric => <MetricChart force_legend={true} key={metric} metric={metric} configs={{
            date_range,
            revalidateOnFocus: false,
            options: {
              dimensions: [metric_category.dimension.key as any],
              filters: {
                [metric_category.dimension.key]: dimension_values
              }
            }
          }} onExpand={() => {
            updateSearchQuery({
              expanded: metric
            });
          }} />)}</Div>}{section.tables && <><Div m={{
            t: 6
          }} grid={{
            columns,
            gap: 6
          }}>{section.tables.map(metric_0 => <MetricTableItem key={metric_0} metric={metric_0} configs={{
              date_range,
              revalidateOnFocus: false,
              options: {
                dimensions: [metric_category.dimension.key as any],
                filters: {
                  [metric_category.dimension.key]: dimension_values
                }
              } as any
            }} max_h={{
              px: 242
            }} />)}</Div></>}</>;
      $[11] = columns;
      $[12] = date_range;
      $[13] = dimension_values;
      $[14] = metric_category.dimension;
      $[15] = updateSearchQuery;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t5 = metric_category.sections.map(t6);
    $[4] = columns;
    $[5] = date_range;
    $[6] = dimension_values;
    $[7] = metric_category.dimension;
    $[8] = metric_category.sections;
    $[9] = updateSearchQuery;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <Div p={6} style={t4}>{t5}</Div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== date_range || $[21] !== dimension_values || $[22] !== expanded_metric || $[23] !== metric_category.dimension || $[24] !== updateSearchQuery) {
    t7 = expanded_metric && <StyledExpandedMetric><MetricChart height={window.innerHeight - page_nav_height} metric={expanded_metric} onExpand={() => {
        updateSearchQuery({
          expanded: undefined
        });
      }} expanded={true} configs={{
        date_range,
        revalidateOnFocus: false,
        options: {
          dimensions: [metric_category.dimension.key as any],
          filters: {
            [metric_category.dimension.key]: dimension_values
          }
        } as any
      }} /></StyledExpandedMetric>;
    $[20] = date_range;
    $[21] = dimension_values;
    $[22] = expanded_metric;
    $[23] = metric_category.dimension;
    $[24] = updateSearchQuery;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t6 || $[27] !== t7) {
    t8 = <StyledViewLayout><StyledViewContent style={t2}>{t6}{t7}</StyledViewContent></StyledViewLayout>;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
};
export default MetricCategoryView;
function _temp(d) {
  return d.resource_id;
}
function _temp2(object, resource_0) {
  return {
    ...object,
    [resource_0.id]: resource_0
  };
}